import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  FlaskConical, 
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  Lock,
  ExternalLink,
  User,
  BookOpen
} from 'lucide-react';
import { studentPortal } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { labLabel } from '../utils/labNaming';
import { format, formatDistanceToNow, isPast, isFuture } from 'date-fns';

function StudentDashboard() {
  const { user } = useAuth();
  
  const { data: labsData, isLoading, error } = useQuery({
    queryKey: ['studentLabs'],
    queryFn: () => studentPortal.getLabs(),
    placeholderData: { labs: [], count: 0 },
  });

  const labs = labsData?.labs || [];
  
  // Categorize labs
  const accessibleLabs = labs.filter(lab => lab.isAccessible && !lab.isD2LLocked);
  const d2lLockedLabs = labs.filter(lab => lab.isD2LLocked);
  const upcomingLabs = labs.filter(lab => lab.accessStatus === 'not_yet_open' && !lab.isD2LLocked);
  const closedLabs = labs.filter(lab => lab.accessStatus === 'closed' && !lab.isD2LLocked);

  if (isLoading) {
    return (
      <div className="animate-fade-in" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh'
      }} role="status" aria-label="Loading dashboard">
        <div className="spinner" style={{ width: 48, height: 48 }} aria-hidden="true" />
        <span style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading your labs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="animate-fade-in" style={{ padding: 40, textAlign: 'center' }} role="alert">
        <AlertCircle size={48} style={{ color: 'var(--accent-danger)', marginBottom: 16 }} aria-hidden="true" />
        <h2>Unable to load labs</h2>
        <p className="text-muted">Please try again later or contact your instructor.</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-purple))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <User size={24} style={{ color: 'white' }} />
          </div>
          <div>
            <h1 style={{ marginBottom: 2 }}>Welcome, {user?.name || 'Student'}</h1>
            <p className="text-muted" style={{ fontSize: 14 }}>
              Student ID: {user?.studentId || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}
        role="region"
        aria-label="Lab statistics"
      >
        <StatCard
          icon={BookOpen}
          label="Active Labs"
          value={accessibleLabs.length}
          color="var(--accent-success)"
        />
        <StatCard
          icon={Clock}
          label="Upcoming"
          value={upcomingLabs.length}
          color="var(--accent-primary)"
        />
        <StatCard
          icon={CheckCircle}
          label="Completed"
          value={closedLabs.length}
          color="var(--accent-purple)"
        />
      </div>

      {/* Active Labs */}
      {accessibleLabs.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={20} style={{ color: 'var(--accent-success)' }} />
            Active Labs
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {accessibleLabs.map(lab => (
              <LabCard key={lab.id} lab={lab} />
            ))}
          </div>
        </section>
      )}

      {/* D2L Locked Labs */}
      {d2lLockedLabs.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Lock size={20} style={{ color: 'var(--accent-warning)' }} />
            D2L Access Required
          </h2>
          <div
            style={{
              padding: 12,
              background: 'var(--accent-warning)08',
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 13,
              color: 'var(--text-secondary)',
              border: '1px solid var(--accent-warning)20'
            }}
            role="note"
          >
            These labs require access through D2L for security. Please launch them from your D2L course page.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {d2lLockedLabs.map(lab => (
              <LabCard key={lab.id} lab={lab} />
            ))}
          </div>
        </section>
      )}

      {/* Upcoming Labs */}
      {upcomingLabs.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={20} style={{ color: 'var(--accent-primary)' }} />
            Upcoming Labs
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {upcomingLabs.map(lab => (
              <LabCard key={lab.id} lab={lab} />
            ))}
          </div>
        </section>
      )}

      {/* Closed Labs */}
      {closedLabs.length > 0 && (
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 18, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Lock size={20} style={{ color: 'var(--text-muted)' }} />
            Past Labs
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {closedLabs.map(lab => (
              <LabCard key={lab.id} lab={lab} />
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {labs.length === 0 && (
        <div className="card" style={{ 
          padding: 60, 
          textAlign: 'center',
          background: 'var(--bg-secondary)'
        }}>
          <FlaskConical size={64} style={{ color: 'var(--text-muted)', marginBottom: 20, opacity: 0.5 }} aria-hidden="true" />
          <h2 style={{ marginBottom: 8 }}>No Labs Assigned</h2>
          <p className="text-muted" style={{ maxWidth: 400, margin: '0 auto' }}>
            You don't have any labs assigned yet. Check back later or contact your instructor if you believe this is an error.
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card" style={{ padding: 16 }} aria-label={`${label}: ${value}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }} aria-hidden="true">
          <Icon size={20} style={{ color }} />
        </div>
        <div>
          <div style={{
            fontSize: 24,
            fontWeight: 600,
            fontFamily: 'var(--font-mono)'
          }}>
            {value}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {label}
          </div>
        </div>
      </div>
    </div>
  );
}

function LabCard({ lab }) {
  const isAccessible = lab.isAccessible && !lab.isD2LLocked;
  const isPastDue = lab.dueDate && isPast(new Date(lab.dueDate)) && isAccessible;
  
  const getStatusBadge = () => {
    // D2L locked takes precedence
    if (lab.isD2LLocked) {
      return <span className="badge" style={{ background: 'var(--accent-warning)', color: 'var(--bg-primary)' }}>D2L Required</span>;
    }
    
    switch (lab.accessStatus) {
      case 'accessible':
        return <span className="badge badge-success">Open</span>;
      case 'not_yet_open':
        return <span className="badge badge-primary">Upcoming</span>;
      case 'closed':
        return <span className="badge badge-secondary">Closed</span>;
      case 'd2l_required':
        return <span className="badge" style={{ background: 'var(--accent-warning)', color: 'var(--bg-primary)' }}>D2L Required</span>;
      default:
        return <span className="badge">{lab.accessStatus}</span>;
    }
  };

  // Determine border color based on lab state
  const getBorderColor = () => {
    if (lab.isD2LLocked) return 'var(--accent-warning)';
    if (isAccessible) return 'var(--accent-success)';
    return 'var(--border-primary)';
  };

  return (
    <div
      className="card"
      style={{
        padding: 0,
        overflow: 'hidden',
        opacity: isAccessible || lab.isD2LLocked ? 1 : 0.7,
        borderLeft: `4px solid ${getBorderColor()}`,
      }}
    >
      <div style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <FlaskConical size={18} style={{ color: 'var(--accent-purple)' }} />
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>
                {lab.name}
              </h3>
              {getStatusBadge()}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
              {lab.courseCode} {lab.section && `• Section ${lab.section}`} • {labLabel(lab)}
            </p>
            {lab.description && (
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 12 }}>
                {lab.description}
              </p>
            )}
          </div>
        </div>

        {/* Dates */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 16, 
          fontSize: 13,
          color: 'var(--text-secondary)',
          marginBottom: 16
        }}>
          {lab.openDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={14} />
              <span>Opens: {format(new Date(lab.openDate), 'MMM d, yyyy h:mm a')}</span>
            </div>
          )}
          {lab.dueDate && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6,
              color: isPastDue ? 'var(--accent-danger)' : 'inherit',
              fontWeight: isPastDue ? 500 : 400
            }}>
              <AlertCircle size={14} />
              <span>
                Due: {format(new Date(lab.dueDate), 'MMM d, yyyy h:mm a')}
                {isPastDue && ' (Past due!)'}
              </span>
            </div>
          )}
          {lab.closeDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lock size={14} />
              <span>Closes: {format(new Date(lab.closeDate), 'MMM d, yyyy h:mm a')}</span>
            </div>
          )}
        </div>

        {/* Instructor */}
        {lab.instructor && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Instructor: {lab.instructor.name} ({lab.instructor.email})
          </div>
        )}

        {/* Action */}
        <div style={{ display: 'flex', gap: 8 }}>
          {lab.isD2LLocked ? (
            <div style={{ 
              padding: '8px 16px', 
              background: 'var(--accent-warning)10',
              border: '1px solid var(--accent-warning)30',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--accent-warning)',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <Lock size={14} />
              Access through D2L only
            </div>
          ) : isAccessible ? (
            <Link
              to={`/labs/${lab.id}`}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <ExternalLink size={16} />
              Open Lab
            </Link>
          ) : (
            <div style={{ 
              padding: '8px 16px', 
              background: 'var(--bg-tertiary)',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--text-muted)'
            }}>
              {lab.accessMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentDashboard;

